// "Trachang" type system: IBM Plex Sans Thai carries all Thai+Latin UI, IBM Plex Mono carries every
// figure and identifier — one superfamily across scripts. Bundled via fontsource so floor terminals
// render the same offline.
import "@fontsource/ibm-plex-sans-thai/400.css";
import "@fontsource/ibm-plex-sans-thai/500.css";
import "@fontsource/ibm-plex-sans-thai/600.css";
import "@fontsource/ibm-plex-sans-thai/700.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/ibm-plex-mono/600.css";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import { createApp } from "vue";
import App from "./App.vue";
import { installErrorHandler } from "./error-handler";
import { watchForIdle } from "./idle-guard";
import { i18n } from "./i18n";
import { preset } from "./preset";
import { queryClient } from "./query-client";
import { router } from "./router";
import { watchForExpiredSession } from "./session-guard";
import { watchTokenAcrossTabs } from "./storage-sync";
import { initThemeMode } from "./theme-mode";
import "./tokens.css";

const app = createApp(App);

// Before anything else runs: a throw during Pinia/router/Vue-Query setup below is exactly the kind
// of thing this exists to catch too, not only a throw from a mounted screen. See
// `error-handler.ts`'s own doc comment for why this needs BOTH `app.config.errorHandler` and a
// boundary independent of Vue's own render pipeline.
installErrorHandler(app);

/**
 * **Unstyled — PrimeVue's headless mode.** None of its design CSS ships; behaviour and a11y stay.
 * `preset.ts` attaches our `.c-*` class hooks through the global pass-through and `tokens.css`
 * styles them, so there is exactly one styling system and one place to change a colour. See
 * `docs/superpowers/specs/2026-08-09-counting-room-redesign.md`.
 */
app.use(PrimeVue, { unstyled: true, pt: preset });
app.use(i18n);
// Client state (session, UI prefs) is Pinia's. Server state (everything from the API) is Vue
// Query's, and the two never overlap — see `features/auth/session.ts`.
//
// Pinia BEFORE the router: the guard in `router.ts` calls `useSession()` on the very first
// navigation, and a store reached before its plugin is installed throws an error naming Pinia
// rather than the ordering that caused it.
app.use(createPinia());
app.use(router);
// Explicit `queryClient`, not the plugin's own default instance — this is what lets
// `features/auth/session.ts`'s `logout()` reach the SAME client a component's `useQueryClient()`
// resolves, and clear it. See `query-client.ts`'s own comment.
app.use(VueQueryPlugin, { queryClient });

// Cross-tab reconciliation: `localStorage` is shared across every tab on this origin, so signing
// out in one must not leave another holding a live session against a token that no longer exists.
// See `storage-sync.ts` for what "reconcile" means here. Wired after Pinia so the handler's
// `useSession()` has an active store to read.
watchTokenAcrossTabs();

// A 401/`STAFF_TOKEN_INVALID` from ANY request — not just the one-shot rehydration runs at page
// load — ends the session and routes to `/login`. See `session-guard.ts`.
watchForExpiredSession();

// Fifteen minutes with no pointer/keyboard input ends the session and asks for the password again
// (design decision 7). See `idle-guard.ts`.
watchForIdle();

// Writes `data-theme` from the OS preference and keeps it in sync on change — `tokens.css`'s
// `[data-theme]` blocks are the only theming system now that PrimeVue ships no CSS of its own.
initThemeMode();

app.mount("#app");
